/**
 * Géométrie d'un croquis de composition : ce qui décide de la forme dessinée
 * dans une vignette de palette, sans rien savoir de qui la dessine.
 *
 * Les deux éditeurs partagent ce module et n'en gardent qu'une enveloppe de
 * rendu — le classique avec ses icônes FontAwesome, le site avec les siennes.
 * Sans ça, la même composition serait croquée deux fois, de deux façons qui
 * finiraient par diverger.
 *
 * Trois formes de descripteur entrent :
 *  - une composition du catalogue (`{ span, box }` / `{ span, rows: [...] }`) ;
 *  - une grille du classique (`{ size, rows }`, où `size` est une fraction) ;
 *  - la forme plate des préréglages du panneau de droite, où `rows` est un
 *    NOMBRE de lignes vides.
 *
 * Une seule forme sort : un arbre de cellules pondérées.
 *
 *   cellule = { weight, box }          // feuille
 *           | { weight, rows: [[cellule]] }
 */

/** Poids d'une cellule dans sa ligne. `span` (douzièmes) et `size` (fraction)
 *  s'y valent : seul leur rapport compte pour un croquis. */
const weightOf = (column) => column?.span ?? column?.size ?? 1;

/** Une ligne sans colonnes décrite reste une ligne : une cellule pleine largeur. */
const rowColumns = (row) => (row?.columns?.length ? row.columns : [{ span: 1 }]);

/**
 * Descripteur de colonne → cellule.
 *
 * @param {object} column
 * @returns {{ weight: number, box?: string, rows?: Array }}
 */
export function sketchCell(column) {
  const weight = weightOf(column);
  const rows = column?.rows;

  // Préréglage plat : un nombre de lignes vides, sans contenu.
  if (typeof rows === 'number') {
    const count = Math.max(rows, 1);
    if (count <= 1) return { weight };
    return {
      weight,
      rows: Array.from({ length: count }, () => [{ weight: 1 }]),
    };
  }

  if (Array.isArray(rows) && rows.length) {
    return {
      weight,
      rows: rows.map((row) => rowColumns(row).map(sketchCell)),
    };
  }

  // La nature de la cellule sort toujours comme une CLÉ, jamais comme l'objet
  // du descripteur : le catalogue partagé la donne déjà en chaîne
  // (`COMPOSITION_BOX`), le document la porte dans `box.type` (un `ItemTypes`).
  // Chaque éditeur garde ainsi sa propre table de glyphes.
  const box = typeof column?.box === 'string' ? column.box : column?.box?.type;
  return box ? { weight, box } : { weight };
}

/** Descripteur de composition (ou de grille) → première ligne de cellules. */
export function sketchCells(columns = []) {
  return columns.map(sketchCell);
}
